import React, { useCallback, useMemo } from 'react';
import Select from 'react-select';
import AiReportTypeField from '../../Components/AiReportTypeField';
import WorkDaysHoursPicker from '../../Components/WorkDaysHoursPicker';
import { CodeIcon } from '../../svg/TemplateIcons';
import TemplateImageEditor from './TemplateImageEditor';
import { useAiReportTypes } from '../../utils/aiReportTypes';
import {
    MAP_ICON_RULES,
    getMapIconUrl,
} from '../../utils/mapIconUpload';

const hardwareOptions = [
    'Arduino', 'BBC Micro:bit', 'ESP32', 'ESP8266', 'Lantronix Fox 3', 'Microduino', 'Onion Omega',
    'Particle', 'Pycom WiPy', 'Raspberry Pi', 'Seeed Wio Terminal', 'Teensy', 'TinyDuino', 'Other'
];
const connectionTypeOptions = ['Ethernet', 'WiFi', 'Satellite', 'GSM'];

const TID_PREFIX_MAIN = 'TMPL';
const TID_PREFIX_DASH = 'TMPL-';

const parseTidParts = (tid) => {
    const raw = String(tid ?? '').replace(/\s+/g, '');
    if (!raw) return { prefix: TID_PREFIX_MAIN, suffix: '' };

    const upper = raw.toUpperCase();
    if (upper.startsWith(TID_PREFIX_DASH)) {
        return { prefix: TID_PREFIX_DASH, suffix: raw.slice(TID_PREFIX_DASH.length) };
    }
    if (upper.startsWith(TID_PREFIX_MAIN)) {
        return { prefix: TID_PREFIX_MAIN, suffix: raw.slice(TID_PREFIX_MAIN.length) };
    }
    return { prefix: TID_PREFIX_MAIN, suffix: raw };
};

const stripTidPrefix = (value) => {
    const raw = String(value ?? '').replace(/\s+/g, '');
    if (!raw) return '';

    const upper = raw.toUpperCase();
    if (upper.startsWith(TID_PREFIX_DASH)) return raw.slice(TID_PREFIX_DASH.length);
    if (upper.startsWith(TID_PREFIX_MAIN)) return raw.slice(TID_PREFIX_MAIN.length);
    return raw;
};

const copyToClipboard = (value) => {
    if (!value) return;
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).catch(() => {});
    }
};

const statusLabel = (device) => {
    const raw = (
        device?.status ??
        device?.connection_status ??
        device?.connectionStatus ??
        device?.is_online ??
        device?.isOnline ??
        device?.online
    );
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'boolean') return raw ? 'Online' : 'Offline';
    if (typeof raw === 'number') return raw === 1 ? 'Online' : 'Offline';
    return 'Unknown';
};

const statusDot = (label) => {
    const value = String(label || '').toLowerCase();
    if (value.includes('online') || value.includes('connected')) return 'var(--td-status-online)';
    if (value.includes('offline') || value.includes('disconnected')) return 'var(--td-status-offline)';
    if (value.includes('error') || value.includes('fail')) return 'var(--td-status-error)';
    return 'var(--td-status-warn)';
};

const TemplateHomeCard = ({ title, subtitle, right, children }) => (
    <div className="td-card" style={{ overflow:'auto' }}>
        <div className="td-card__header">
            <div className="td-card__header-left">
                <div className="td-card__title-row">
                    <h5 className="td-card__title">{title}</h5>
                    {subtitle ? (
                        <span className="td-card__subtitle">{subtitle}</span>
                    ) : null}
                </div>
            </div>
            {right}
        </div>

        <div className="td-card__spacer" />
        <div className="td-card__body">{children}</div>
    </div>
);

const TemplateHomeField = ({ label, value, action, multiline }) => (
    <section className="td-field">
        <div className="td-field__top">
            <span className="td-field__label">{label}</span>
            {action}
        </div>

        <div className={multiline ? "td-field__box td-field__box--multiline" : "td-field__box"}>
            <div
                className={
                    multiline
                        ? (value ? "td-field__value td-field__value--multiline" : "td-field__value td-field__value--multiline td-field__value--empty")
                        : (value ? "td-field__value" : "td-field__value td-field__value--empty")
                }
            >
                {value || '-'}
            </div>
        </div>
    </section>
);

const TemplateHomeSettingsPanel = ({
    t,
    template,
    templateSettingText,
    edit,
    canEditTemplate,
    canManageTemplateExampleLinks,
    setField,
    hardwareSelectOptions,
    connectionTypeSelectOptions,
    selectedHardwareOption,
    selectedConnectionTypeOption,
    selectStyles,
    onOpenAttachExampleDialog,
    onDetachTemplateFromExample,
    pendingMapIconPreviewUrl,
    pendingMapIconFile,
    onMapIconFileChange,
    onRemoveMapIcon,
    onTemplateImageFileChange,
    onRemoveTemplateImage,
}) => {
    const { options: aiReportTypeOptions, availability: aiReportAvailability } = useAiReportTypes();
    const showAiReportTypeField =
        aiReportAvailability.templates !== false ||
        aiReportTypeOptions.length > 0 ||
        Boolean(template?.type);
    const tidParts = parseTidParts(template?.tid);
    const tidPrefix = tidParts.prefix;
    const tidSuffix = tidParts.suffix;
    const tidSuffixMax = Math.max(1, 50 - String(tidPrefix || '').length);
    const attachedTemplateExample = template?.templateExample || null;
    const attachedTemplateExampleName =
        attachedTemplateExample?.example_name ||
        attachedTemplateExample?.name ||
        '';

    return (
        <TemplateHomeCard
            title={t.TemplateSettings?.settings || 'Template settings'}
            subtitle={templateSettingText || ''}
            right={null}
        >
            {edit.mode === 'edit' && canEditTemplate ? (
                <div className="td-edit-grid">
                    <div>
                        <div className="td-edit-label">
                            {t.TemplateSettings?.nameLabel || 'Name'}
                        </div>
                        <input
                            type="text"
                            maxLength={50}
                            value={template.name || ''}
                            onChange={(e) => setField('name', e.target.value)}
                            className="add-column td-input"
                        />
                    </div>

                    <div>
                        <div className="td-edit-label">
                            {t.TemplateSettings?.templateIdLabel || 'Template ID'}
                        </div>
                        <div
                            className="add-column td-input"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '0 12px'
                            }}
                        >
                            <span
                                style={{
                                    fontWeight: 700,
                                    opacity: 0.85,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {tidPrefix}
                            </span>

                            <input
                                type="text"
                                maxLength={tidSuffixMax}
                                value={tidSuffix}
                                onChange={(e) => {
                                    const v = stripTidPrefix(e.target.value);
                                    if (!v.includes(' ')) {
                                        setField('tid', v ? `${tidPrefix}${v}` : '');
                                    }
                                }}
                                className="add-column"
                                style={{
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    padding: 0,
                                    flex: 1,
                                    minWidth: 0
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === ' ') e.preventDefault();
                                }}
                            />
                        </div>
                    </div>

                    <div>
                        <div className="td-edit-label">
                            {t.TemplateSettings?.hardwareLabel || 'Hardware'}
                        </div>
                        <Select
                            options={hardwareSelectOptions}
                            value={selectedHardwareOption}
                            onChange={(opt) => setField('hardware', opt ? opt.value : null)}
                            classNamePrefix="react-select"
                            styles={selectStyles}
                        />
                    </div>

                    <div>
                        <div className="td-edit-label">
                            {t.TemplateSettings?.connectionTypeLabel || 'Connection type'}
                        </div>
                        <Select
                            options={connectionTypeSelectOptions}
                            value={selectedConnectionTypeOption}
                            onChange={(opt) => setField('connectionType', opt ? opt.value : null)}
                            classNamePrefix="react-select"
                            styles={selectStyles}
                        />
                    </div>

                    <div>
                        <div className="td-edit-label">
                            {t.TemplateSettings?.hotspotPrefixLabel || 'Hotspot prefix'}
                        </div>
                        <input
                            type="text"
                            maxLength={50}
                            value={template.hotspotPrefix || ''}
                            onChange={(e) => setField('hotspotPrefix', e.target.value)}
                            className="add-column td-input"
                        />
                    </div>

                    <TemplateImageEditor
                        label={t.TemplateSettings?.iconLabel || 'Template image'}
                        image={template.icon}
                        onChange={onTemplateImageFileChange}
                        onRemove={onRemoveTemplateImage}
                        uploadText={t.TemplateSettings?.uploadLogo || 'Upload image'}
                        helperText={t.TemplateSettings?.uploadHelper || 'PNG, JPG, WebP or SVG.'}
                    />

                    <div>
                        <div className="td-edit-label">
                            {t.TemplateSettings?.mapIconLabel || 'Map icon'}
                        </div>
                        <div className="template-map-icon-uploader">
                            <label className="template-map-icon-drop">
                                <input
                                    type="file"
                                    accept={MAP_ICON_RULES.accept}
                                    onChange={onMapIconFileChange}
                                />
                                {pendingMapIconPreviewUrl || template.map_icon ? (
                                    <img
                                        src={pendingMapIconPreviewUrl || getMapIconUrl(template.map_icon)}
                                        alt=""
                                        className="template-map-icon-preview"
                                    />
                                ) : (
                                    <span>{t.TemplateSettings?.uploadMapIcon || 'Upload map icon'}</span>
                                )}
                            </label>
                            <div className="template-map-icon-meta">
                                <div>
                                    {pendingMapIconFile?.name ||
                                        t.TemplateSettings?.mapIconRules ||
                                        'PNG, JPG, WebP, or SVG. 24-512px. Max 512KB.'}
                                </div>
                                {pendingMapIconPreviewUrl || template.map_icon ? (
                                    <button type="button" className="td-secondary-btn" onClick={onRemoveMapIcon}>
                                        {t.TemplateSettings?.removeMapIcon || 'Remove'}
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {showAiReportTypeField && (
                        <AiReportTypeField
                            value={template.type || ''}
                            onChange={(value) => setField('type', value || null)}
                            options={aiReportTypeOptions}
                            styles={selectStyles}
                            label="AI Report Type"
                            placeholder="Select AI report type"
                            section={false}
                            labelClassName="td-edit-label"
                            itemClassName=""
                            classNamePrefix="react-select"
                        />
                    )}

                    <div>
                        <div className="td-edit-label">
                            {t.TemplateSettings?.descriptionLabel || 'Description'}
                        </div>
                        <textarea
                            className="add-column textarea-large td-textarea"
                            maxLength={128}
                            rows={5}
                            value={template.description || ''}
                            onChange={(e) => setField('description', e.target.value)}
                            placeholder={t.TemplateSettings?.descriptionLabel || 'Description'}
                        />
                    </div>

                    <WorkDaysHoursPicker
                        value={template.work_days_hours || []}
                        onChange={(nextRows) => setField('work_days_hours', nextRows)}
                        labels={{
                            title: t.TemplateSettings?.workDaysHoursTitle || 'Work days and hours',
                            sameShift:
                                t.TemplateSettings?.sameShift ||
                                'Use same shift start / shift end for all work days',
                        }}
                    />
                </div>
            ) : (
                <div>
                    <TemplateHomeField
                        label={t.TemplateSettings?.nameLabel || 'Name'}
                        value={template.name || '-'}
                        action={
                            template.name ? (
                                <button
                                    type="button"
                                    onClick={() => copyToClipboard(template.name)}
                                    className="td-circle-btn"
                                    title="Copy"
                                >
                                    <CodeIcon />
                                </button>
                            ) : null
                        }
                    />
                    <TemplateHomeField
                        label={t.TemplateSettings?.templateIdLabel || 'Template ID'}
                        value={template.tid || '-'}
                        action={
                            template.tid ? (
                                <button
                                    type="button"
                                    onClick={() => copyToClipboard(template.tid)}
                                    className="td-circle-btn"
                                    title="Copy"
                                >
                                    <CodeIcon />
                                </button>
                            ) : null
                        }
                    />
                    <TemplateHomeField
                        label={t.attachedExampleLabel || 'Attached Example'}
                        value={attachedTemplateExampleName || t.attachedExampleNone || 'Not attached'}
                        action={
                            canManageTemplateExampleLinks ? (
                                <div className="td-example-link-actions">
                                    <button
                                        type="button"
                                        className="td-primary-btn td-primary-btn--compact"
                                        onClick={onOpenAttachExampleDialog}
                                    >
                                        {attachedTemplateExample
                                            ? t.changeAttachedExample || 'Change Example'
                                            : t.attachToExample || 'Attach to Example'}
                                    </button>
                                    {attachedTemplateExample ? (
                                        <button
                                            type="button"
                                            className="td-secondary-btn"
                                            onClick={onDetachTemplateFromExample}
                                        >
                                            {t.detachExample || 'Detach'}
                                        </button>
                                    ) : null}
                                </div>
                            ) : null
                        }
                    />
                    {attachedTemplateExample ? (
                        <div className="td-example-link-helper">
                            {t.attachedExampleHelper ||
                                'Changes made to this template example will sync here automatically until you detach it.'}
                        </div>
                    ) : null}
                    <TemplateHomeField
                        label={t.TemplateSettings?.templateSettingLabel || 'Template setting'}
                        value={templateSettingText || '-'}
                    />
                    <TemplateHomeField
                        label={t.TemplateSettings?.mapIconLabel || 'Map icon'}
                        value={template.map_icon || template.icon || '-'}
                    />
                    <TemplateHomeField
                        label="AI Report Type"
                        value={aiReportTypeOptions.find((option) => option.value === template.type)?.label || '-'}
                    />
                    <TemplateHomeField
                        label={t.TemplateSettings?.descriptionLabel || 'Description'}
                        value={template.description || '-'}
                        multiline
                    />
                </div>
            )}
        </TemplateHomeCard>
    );
};

const TemplateHomeDevicesPanel = ({
    t,
    devices,
    devicesCount,
    isRtl,
    navigate,
    canProvisionDevices,
    onOpenAddDevice
}) => {
    const getDeviceDisplayName = useCallback(
        (device) => device?.name || device?.device_name || device?.label || `#${device?.id ?? ''}`,
        [],
    );

    const deviceNameColumnWidth = useMemo(() => {
        const labels = [
            t.deviceNameLabel || 'Device name',
            ...(Array.isArray(devices) ? devices : []).map((device) => getDeviceDisplayName(device)),
        ].filter((label) => String(label || '').trim() !== '');

        const longest = labels.reduce(
            (currentLongest, label) => (String(label).length > String(currentLongest).length ? label : currentLongest),
            labels[0] || '',
        );

        const fallbackWidth = Math.max(160, String(longest).length * 8 + 28);
        if (typeof document === 'undefined') {
            return Math.min(560, fallbackWidth);
        }

        const measureCanvas = document.createElement('canvas');
        const context = measureCanvas.getContext('2d');
        if (!context) {
            return Math.min(560, fallbackWidth);
        }

        context.font = '800 13px "Segoe UI", sans-serif';
        const measuredWidth = Math.ceil(context.measureText(String(longest)).width + 30);
        return Math.min(560, Math.max(160, measuredWidth));
    }, [devices, getDeviceDisplayName, t.deviceNameLabel]);

    const deviceNameGridTemplate = `minmax(${deviceNameColumnWidth}px, 1fr) 140px`;

    return (
        <TemplateHomeCard
            title={t.homeHeader || 'Home'}
            subtitle={`${devicesCount} ${t.devicesLabel || 'Devices'}`}
            right={
                canProvisionDevices ? (
                    <button
                        type="button"
                        onClick={() => onOpenAddDevice && onOpenAddDevice()}
                        className="td-primary-btn"
                    >
                        <span className="td-primary-btn__plus">+</span>
                        <span>{t.newDevice || 'New Device'}</span>
                    </button>
                ) : null
            }
        >
            <div className="td-devices">
                <div className="td-devices__header" style={{ gridTemplateColumns: deviceNameGridTemplate }}>
                    <div>{t.deviceNameLabel || 'Device name'}</div>
                    <div style={{ textAlign: isRtl ? 'left' : 'right' }}>
                        {t.statusLabel || 'Status'}
                    </div>
                </div>

                {devices.length === 0 ? (
                    <div className="td-devices__empty">
                        {t.noDevices || 'No devices linked to this template yet.'}
                    </div>
                ) : (
                    <div className="td-devices__list">
                        {devices.map((device, index) => {
                            const label = statusLabel(device);
                            const dot = statusDot(label);
                            return (
                                <button
                                    key={device.id ?? index}
                                    type="button"
                                    onClick={() => navigate(`/devices/details/${device.id}`)}
                                    className={
                                        index % 2 === 0
                                            ? 'td-device-row td-device-row--even'
                                            : 'td-device-row td-device-row--odd'
                                    }
                                    style={{ gridTemplateColumns: deviceNameGridTemplate }}
                                >
                                    <div
                                        className="td-device-row__name"
                                        title={getDeviceDisplayName(device)}
                                    >
                                        {getDeviceDisplayName(device)}
                                    </div>

                                    <div
                                        className="td-device-row__status"
                                        style={{
                                            justifyContent: isRtl ? 'flex-start' : 'flex-end'
                                        }}
                                    >
                                        <span
                                            className="td-device-row__dot"
                                            style={{ backgroundColor: dot }}
                                        />
                                        <span className="td-device-row__label">{label}</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </TemplateHomeCard>
    );
};

const TemplateHomeTab = ({
    details,
    setDetails,
    edit,
    canEditTemplate,
    canProvisionDevices,
    t,
    isRtl,
    navigate,
    onOpenAddDevice,
    canManageTemplateExampleLinks,
    onOpenAttachExampleDialog,
    onDetachTemplateFromExample,
    pendingMapIconPreviewUrl,
    pendingMapIconFile,
    onMapIconFileChange,
    onRemoveMapIcon,
    onTemplateImageFileChange,
    onRemoveTemplateImage,
}) => {
    if (!details) return null;

    const template = details;

    const devices = Array.isArray(template.templateDevices)
        ? template.templateDevices
        : (Array.isArray(template.devices) ? template.devices : []);

    const devicesCount = typeof template.device_count === 'number'
        ? template.device_count
        : devices.length;

    const templateSettingText = [
        template.hardware,
        template.connectionType || template.connection
    ].filter(Boolean).join(', ');

    const hardwareSelectOptions = hardwareOptions.map(option => ({ value: option, label: option }));
    const connectionTypeSelectOptions = connectionTypeOptions.map(option => ({ value: option, label: option }));

    const selectedHardwareOption =
        hardwareSelectOptions.find(option => option.value === template.hardware) || null;

    const selectedConnectionTypeOption =
        connectionTypeSelectOptions.find(option => option.value === template.connectionType) || null;

    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: 'var(--td-select-control-bg)',
            borderColor: state.isFocused ? 'var(--td-select-border-focus)' : 'var(--td-select-border)',
            boxShadow: state.isFocused ? '0 0 0 1px var(--td-select-focus-ring)' : 'none',
            '&:hover': { borderColor: 'var(--td-select-border-focus)' },
            color: 'var(--td-select-text)',
            fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
            minHeight: '42px',
            borderRadius: 10,
            cursor: edit.mode === 'edit' && canEditTemplate ? 'pointer' : 'default',
            opacity: edit.mode === 'edit' && canEditTemplate ? 1 : 0.75
        }),
        singleValue: provided => ({
            ...provided,
            color: 'var(--td-select-text)',
            fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))'
        }),
        input: provided => ({
            ...provided,
            color: 'var(--td-select-text)',
            fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))'
        }),
        placeholder: provided => ({
            ...provided,
            color: 'var(--td-select-placeholder)',
            fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))'
        }),
        menu: provided => ({
            ...provided,
            backgroundColor: 'var(--td-select-menu-bg)',
            border: '1px solid var(--td-select-border)',
            borderRadius: 10,
            overflow: 'hidden',
            zIndex: 9999
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? 'red' : state.isFocused ? 'blue' : 'var(--td-selected-option-unfocused)',
            color: state.isSelected ? 'var(--td-select-option-selected-text)' : 'var(--td-select-text)',
            fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
            '&:active': { backgroundColor: 'var(--td-select-option-active-bg)' },
            cursor: 'pointer'
        }),
        dropdownIndicator: provided => ({
            ...provided,
            color: 'var(--td-select-text)',
            paddingRight: 12,
            '&:hover': { color: 'var(--td-select-border-focus)' }
        }),
        clearIndicator: provided => ({
            ...provided,
            color: 'var(--td-select-text)',
            '&:hover': { color: 'var(--td-select-border-focus)' }
        }),
        indicatorSeparator: () => ({ display: 'none' })
    };

    const setField = (key, value) => setDetails({ ...template, [key]: value });

    return (
        <div className="asset-card-page td-home-page">
            <div className="td-home-grid">
                <TemplateHomeDevicesPanel
                    t={t}
                    devices={devices}
                    devicesCount={devicesCount}
                    isRtl={isRtl}
                    navigate={navigate}
                    canProvisionDevices={canProvisionDevices}
                    onOpenAddDevice={onOpenAddDevice}
                />

                <TemplateHomeSettingsPanel
                    t={t}
                    template={template}
                    templateSettingText={templateSettingText}
                    edit={edit}
                    canEditTemplate={canEditTemplate}
                    canManageTemplateExampleLinks={canManageTemplateExampleLinks}
                    setField={setField}
                    hardwareSelectOptions={hardwareSelectOptions}
                    connectionTypeSelectOptions={connectionTypeSelectOptions}
                    selectedHardwareOption={selectedHardwareOption}
                    selectedConnectionTypeOption={selectedConnectionTypeOption}
                    selectStyles={selectStyles}
                    onOpenAttachExampleDialog={onOpenAttachExampleDialog}
                    onDetachTemplateFromExample={onDetachTemplateFromExample}
                    pendingMapIconPreviewUrl={pendingMapIconPreviewUrl}
                    pendingMapIconFile={pendingMapIconFile}
                    onMapIconFileChange={onMapIconFileChange}
                    onRemoveMapIcon={onRemoveMapIcon}
                    onTemplateImageFileChange={onTemplateImageFileChange}
                    onRemoveTemplateImage={onRemoveTemplateImage}
                />
            </div>
        </div>
    );
};

export default TemplateHomeTab;
